import User from '@/models/user'

export type ResolvedJobFollowers = {
  /** Follower user uuids the form can show and edit */
  followerIds: string[]
  /**
   * Placeholder ids (in followerIds) for legacy sids matching no known user record, so they show as
   * removable chips. Never sent as follower ids: their sid is in sidsById.
   */
  unresolvedIds: string[]
  /**
   * Legacy sids matching known records, mapped to every matching uuid. All are selected because the sid
   * can't say which records follow the job: a multi-org user has several, and the users API may hide the
   * one that does, so even a single match is unverified.
   */
  legacyIds: Record<string, string[]>
  /**
   * The job's sid for each follower uuid, so an id the users list doesn't know (e.g. a hidden
   * multi-org record) still has a sid to send to an API that predates follower_ids
   */
  sidsById: Record<string, string>
}

type FollowersSource = { followers: string[]; followerIds: string[] }

/**
 * An API that predates follower_ids returns followers as sids only (a current API returns an id for
 * every follower). Resolve those sids into user uuids so the picker can show and remove them.
 */
export function resolveJobFollowers(
  job: FollowersSource,
  allUsers: Record<string, User>,
): ResolvedJobFollowers {
  if (job.followerIds.length || !job.followers.length) {
    // The API builds both lists from the same followers, so they pair up by position
    const sidsById = Object.fromEntries(job.followerIds.map((id, i) => [id, job.followers[i]]))
    return { followerIds: job.followerIds, unresolvedIds: [], legacyIds: {}, sidsById }
  }

  const users = Object.values(allUsers)
  const resolved: ResolvedJobFollowers = {
    followerIds: [],
    unresolvedIds: [],
    legacyIds: {},
    sidsById: {},
  }

  for (const sid of new Set(job.followers)) {
    const ids = users.filter((u) => u.id === sid).map((u) => u.uuid)

    if (ids.length === 0) {
      // Deterministic, so change detection matches what setJobProperties loaded
      const placeholderId = `unresolved:${sid}`
      resolved.unresolvedIds.push(placeholderId)
      resolved.sidsById[placeholderId] = sid
      ids.push(placeholderId)
    } else {
      resolved.legacyIds[sid] = ids
    }
    resolved.followerIds.push(...ids)
  }

  return resolved
}

/**
 * Whether the selection still holds every record of a legacy sid, i.e. the user hasn't changed that
 * person. Until they do, the save must go by sid so the records already following are kept.
 */
export function hasUntouchedLegacyFollowers(
  followerIds: string[],
  legacyIds: Record<string, string[]>,
  allUsers: Record<string, User>,
): boolean {
  return Object.entries(legacyIds).some(([sid, ids]) => {
    const selected = followerIds.filter((id) => allUsers[id]?.id === sid)
    return selected.length === ids.length && ids.every((id) => selected.includes(id))
  })
}

/**
 * Display names of a job's followers. Looks up by user id, since a multi-org user's records share one
 * sid; falls back to sids for an API that predates follower_ids, or for an id the users list doesn't
 * know (e.g. a hidden multi-org record, whose visible records share its sid and name).
 */
export function getFollowerNames(
  job: FollowersSource,
  allUsers: Record<string, User>,
  getUserBySid: (sid: string) => User | null,
): string[] {
  const getSidName = (sid: string | undefined) => (sid ? getUserBySid(sid)?.name : undefined)
  // The API builds both lists from the same followers, so they pair up by position
  const names = job.followerIds.length
    ? job.followerIds.map((id, i) => allUsers[id]?.name ?? getSidName(job.followers[i]))
    : job.followers.map(getSidName)

  return names.map((name) => name ?? 'Unknown User')
}

/**
 * Applies a picker change per person rather than per record: deselecting any record of a sid removes
 * all of its records, and selecting one adds all of them. Used while followers save by sid, which can't
 * express a choice between a multi-org user's records, so the picker shows exactly what will be saved.
 */
export function applyFollowerChangeBySid(
  previousIds: string[],
  nextIds: string[],
  allUsers: Record<string, User>,
): string[] {
  const sidOf = (id: string) => allUsers[id]?.id
  const removedSids = new Set(previousIds.filter((id) => !nextIds.includes(id)).map(sidOf))
  const addedSids = new Set(nextIds.filter((id) => !previousIds.includes(id)).map(sidOf))
  removedSids.delete(undefined)
  addedSids.delete(undefined)

  const kept = nextIds.filter((id) => !removedSids.has(sidOf(id)))
  const added = Object.values(allUsers)
    .filter((u) => addedSids.has(u.id) && !removedSids.has(u.id) && !kept.includes(u.uuid))
    .map((u) => u.uuid)

  return [...kept, ...added]
}
